import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Drawer, Skeleton, StatusTag } from '@/components/ui';
import type { OrgAdminRow, Organization, OrgTenant } from '@/api/orgAdmin';
import { OrgAvatar } from './OrgAvatar';
import { formatCountVN } from './orgFormat';
import styles from './OrganizationPeekDrawer.module.css';

interface OrganizationPeekDrawerProps {
    org: Organization | null;
    tenants: OrgTenant[] | undefined;
    tenantsLoading: boolean;
    admins: OrgAdminRow[] | undefined;
    adminsLoading: boolean;
    accountCount: number | undefined;
    onClose: () => void;
    onOpenDetail: (org: Organization) => void;
    onNavigateTab: (org: Organization, tab: 'tenants' | 'admins' | 'branding') => void;
    onCreateTenant: (org: Organization) => void;
}

/**
 * EAA-1815: the list's "quick peek" — frame 21 lines 750-1049 (the 400px
 * aside). A `Drawer` (antd, right placement) rather than a permanent aside:
 * "detail is a full page … the drawer survives only as a quick peek from the
 * list" (UX-SPEC). Only "Tổng quan" renders content here; the other three
 * tabs are direct links into the full detail page's own tabs (URL-backed
 * there) rather than a second copy of that content living in the drawer too.
 */
export function OrganizationPeekDrawer({
    org,
    tenants,
    tenantsLoading,
    admins,
    adminsLoading,
    accountCount,
    onClose,
    onOpenDetail,
    onNavigateTab,
    onCreateTenant,
}: OrganizationPeekDrawerProps) {
    const { t } = useTranslation();
    const tenantStatusConfig = useMemo(
        () => ({
            active: { label: t('admin.organizations.status.active'), tone: 'success' as const },
            inactive: {
                label: t('admin.organizations.statusTabs.inactive'),
                tone: 'default' as const,
            },
        }),
        [t]
    );

    if (!org) return null;

    const tenantCount = tenants?.length;
    const brandColor = org.branding?.primary_color;

    return (
        <Drawer
            open={Boolean(org)}
            onClose={onClose}
            width={400}
            closeIcon={false}
            title={
                <span className={styles.header}>
                    <OrgAvatar name={org.name} size={40} tone="accent" />
                    <span className={styles.identity}>
                        <span className={styles.name}>{org.name}</span>
                        <code className={styles.code}>{org.code}</code>
                    </span>
                    {/* EAA-1804: the app's last classless raw `<button>`. It hand-rolled a
                        borderless icon button in inline styles — which is the case the kit rule
                        names directly: a hand-styled raw tag has to earn its focus ring, hover
                        and disabled treatment again, by hand, where a future theme pass will not
                        find it. `type="text"` is antd's borderless variant, so the paint comes
                        from the theme tokens instead. Layout stays inline (`flex: none`) because
                        that is a fact about this header row, not about the button. */}
                    <Button
                        type="text"
                        style={{ flex: 'none', padding: 0, color: 'var(--color-text-muted)' }}
                        aria-label={t('common.close')}
                        onClick={onClose}
                        icon={
                            <svg
                                viewBox="0 0 24 24"
                                width="18"
                                height="18"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                aria-hidden="true"
                            >
                                <path d="M6 6l12 12M18 6 6 18" strokeLinecap="round" />
                            </svg>
                        }
                    />
                </span>
            }
            styles={{ body: { display: 'flex', flexDirection: 'column', gap: 0, padding: 0 } }}
            // EAA-1804: kit `Button` instead of hand-written `<button className="portal-btn">`.
            // This comment used to add that "the close icon button in the title above keeps its
            // own bespoke styling" — no longer true, that button is a kit `Button type="text"`
            // now (see above). The peek-tabs below DO keep their own styling: they are a
            // segmented tab strip, one of four such strips in this app, and converting them
            // belongs with that sweep rather than here.
            footer={
                <div className={styles.footer}>
                    <Button type="primary" style={{ flex: 1 }} onClick={() => onOpenDetail(org)}>
                        {t('admin.organizations.openOrgPage')}
                    </Button>
                    <Button onClick={() => onCreateTenant(org)}>
                        {t('admin.organizations.addTenantAction')}
                    </Button>
                </div>
            }
        >
            <div className={styles.tabs}>
                <span className={`${styles.tab} is-active`}>
                    {t('admin.organizations.detail.tabs.overview')}
                </span>
                <button
                    type="button"
                    className={styles.tab}
                    onClick={() => onNavigateTab(org, 'tenants')}
                >
                    {t('admin.organizations.tenantsLabel')}
                </button>
                <button
                    type="button"
                    className={styles.tab}
                    onClick={() => onNavigateTab(org, 'admins')}
                >
                    {t('admin.organizations.detail.tabs.admins')}
                </button>
                <button
                    type="button"
                    className={styles.tab}
                    onClick={() => onNavigateTab(org, 'branding')}
                >
                    {t('admin.organizations.detail.tabs.branding')}
                </button>
            </div>

            <div style={{ flex: 1, padding: 20, display: 'flex', flexDirection: 'column', gap: 20 }}>
                <div className={styles.stats}>
                    <div className={styles.stat}>
                        <span className={styles.statLabel}>
                            {t('admin.organizations.tenantsLabel')}
                        </span>
                        <span className={styles.statNum}>
                            {tenantCount === undefined
                                ? t('admin.organizations.pendingValuePlaceholder')
                                : formatCountVN(tenantCount)}
                        </span>
                    </div>
                    <div className={styles.stat}>
                        <span className={styles.statLabel}>
                            {t('admin.organizations.detail.tabs.admins')}
                        </span>
                        <span className={styles.statNum}>
                            {adminsLoading || admins === undefined
                                ? t('admin.organizations.pendingValuePlaceholder')
                                : formatCountVN(admins.length)}
                        </span>
                    </div>
                </div>

                {accountCount !== undefined && (
                    <p className={styles.asideHint} style={{ margin: 0 }}>
                        {t('admin.organizations.peekDrawer.accountsInOrgHint', {
                            value: formatCountVN(accountCount),
                        })}
                    </p>
                )}

                <div className={styles.section}>
                    <span className={styles.sectionLabel}>
                        {t('admin.organizations.tenantsLabel')}
                    </span>
                    {tenantsLoading && (
                        <div role="status" aria-live="polite">
                            <span className="portal-sr-only">
                                {t('admin.organizations.loadingSrOnly')}
                            </span>
                            <Skeleton height="2.5rem" />
                        </div>
                    )}
                    {!tenantsLoading && tenantCount === 0 && (
                        <p className={styles.noTenantCallout}>
                            {t('admin.organizations.peekDrawer.noTenantShort')}
                        </p>
                    )}
                    {!tenantsLoading &&
                        (tenants ?? []).slice(0, 4).map((tenant) => (
                            <div className={styles.tenantRow} key={tenant.id}>
                                <span style={{ flex: 1, minWidth: 0 }}>
                                    <span className={styles.tenantName}>
                                        {tenant.name ?? tenant.tenant_id}
                                    </span>
                                    <br />
                                    <span className={styles.tenantSlug}>
                                        {tenant.slug}
                                    </span>
                                </span>
                                <StatusTag
                                    status={tenant.tenant_is_active ? 'active' : 'inactive'}
                                    config={tenantStatusConfig}
                                />
                            </div>
                        ))}
                </div>

                <div className={styles.section}>
                    <span className={styles.sectionLabel}>
                        {t('admin.organizations.peekDrawer.brandingSectionLabel')}
                    </span>
                    <div className={styles.brandRow}>
                        <span
                            className={styles.brandSwatch}
                            style={{ background: brandColor || 'var(--color-primary)' }}
                            aria-hidden="true"
                        />
                        <span style={{ flex: 1, minWidth: 0 }}>
                            <code style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 13 }}>
                                {brandColor
                                    ? brandColor.toUpperCase()
                                    : t('admin.organizations.defaultBrandLabel')}
                            </code>
                            <br />
                            <span className={styles.asideHint}>
                                {t('admin.organizations.peekDrawer.brandingApplyHint')}
                            </span>
                        </span>
                        <Button onClick={() => onNavigateTab(org, 'branding')}>
                            {t('common.edit')}
                        </Button>
                    </div>
                </div>
            </div>
        </Drawer>
    );
}

export default OrganizationPeekDrawer;
